import { FC, useContext, useMemo, useState } from "react";
import classNames from "classnames";
import { TranslationScopes } from "@finbackoffice/enums";
import { IGameData, useRuntimeConfig, useSportsbookConfig } from "@finbackoffice/site-core";
import { SportsbookLayoutEnum } from "@finbackoffice/fe-core";
import { SIRWidgetContext } from "contexts";
import Translate from "components/base/translate/Translate";
import ErrorBoundary from "components/base/error-boundary/ErrorBoundary";
import FadeInAnimation from "components/base/fade-in/FadeInAnimation";
import { SIRWidgetCore } from "components/base/widgets/sir/SIRWidgetCore";
import { SIRWidgetPrematch } from "components/base/widgets/sir/SIRWidgetPrematch";
import { DEFAULT_DATE_FORMAT, FeedTypeEnum, useDate } from "hooks";
import TeamIcon from "../../base/team-icon/TeamIcon";
import { GameVariantType } from "../../base/game/Game";
import styles from "./prematch-game-header.module.sass";

interface IProps {
    gameData: IGameData;
    variant: GameVariantType;
}

const PrematchGameHeader: FC<IProps> = ({ gameData, variant }) => {
    const COMMON_SITE_CONFIGS = useRuntimeConfig("COMMON_SITE_CONFIGS");
    const ASSETS_URL = useRuntimeConfig("ASSETS_URL");
    const sportsbookConfig = useSportsbookConfig();
    const isFlatLayout = sportsbookConfig.type === SportsbookLayoutEnum.Flat;
    const { checkSupportedCompactSport } = useContext(SIRWidgetContext);
    const [showWidget] = useState(checkSupportedCompactSport?.(gameData.sport_id));
    const [loading, setLoading] = useState<boolean>(true);
    const [widgetError, setWidgetError] = useState<boolean>(false);
    const { formatDate } = useDate();

    const defaultContent = useMemo(
        () => (
            <div className={styles.prematchScoreboard}>
                <div className={styles.odds}>
                    <div>
                        <TeamIcon sportId={gameData.sport_id} teamId={gameData.home_team?.id} />
                        <span className={styles.teamName}>{gameData.home_team?.name}</span>
                    </div>
                    <div>
                        <span className={styles.teamName}>{gameData.away_team?.name}</span>
                        <TeamIcon sportId={gameData.sport_id} teamId={gameData.away_team?.id} />
                    </div>
                </div>
                <div className={styles.betsClose}>
                    <Translate tid="prematch_betsCloseAt" namespace={TranslationScopes.PreMatch} />{" "}
                    {gameData.scheduled_at &&
                        formatDate(new Date(gameData.scheduled_at), `${DEFAULT_DATE_FORMAT} HH:mm`)}
                </div>
            </div>
        ),
        [
            formatDate,
            gameData.away_team?.id,
            gameData.away_team?.name,
            gameData.home_team?.id,
            gameData.home_team?.name,
            gameData.scheduled_at,
            gameData.sport_id,
        ],
    );

    const backgroundUrl = useMemo(() => {
        const dir = isFlatLayout ? "scoreboards-flat" : "scoreboards";
        const url = `url(${ASSETS_URL}/common/mobile/${dir}/${gameData.sport_id}.webp) no-repeat bottom center`;

        return url;
    }, [ASSETS_URL, gameData.sport_id, isFlatLayout]);

    return (
        <ErrorBoundary name={PrematchGameHeader.name}>
            <FadeInAnimation>
                <section
                    className={classNames(
                        isFlatLayout ? styles.prematchGameHeaderFlat : styles.prematchGameHeader,
                        styles[`sportId-${gameData.sport_id}`],
                        !loading && showWidget && !widgetError && styles.prematchGameHeaderWidget,
                    )}
                    style={{
                        background: backgroundUrl,
                    }}>
                    {COMMON_SITE_CONFIGS.liveMatchTracker ? (
                        <>
                            {showWidget && (
                                <SIRWidgetPrematch
                                    channel={FeedTypeEnum.prematch}
                                    matchId={`${gameData?.id}`}
                                    sportId={gameData.sport_id}
                                    regionId={gameData.category_id}
                                    onError={() => setWidgetError(true)}
                                    onLoad={() => setLoading(false)}
                                    containerSelector={
                                        variant === "modal" ? "#modal-priority" : "#mainContent"
                                    }>
                                    {(isLicenced, loading, showFullWidget) => (
                                        <SIRWidgetCore
                                            sportId={gameData.sport_id}
                                            isLicenced={isLicenced}
                                            loading={loading}
                                            showFullWidget={showFullWidget}
                                        />
                                    )}
                                </SIRWidgetPrematch>
                            )}
                            {(loading || widgetError) && defaultContent}
                        </>
                    ) : (
                        defaultContent
                    )}
                </section>
            </FadeInAnimation>
        </ErrorBoundary>
    );
};

export default PrematchGameHeader;
